import { BookOpen, Home, Users, Sprout } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const MILESTONES = [
  {
    icon: BookOpen,
    title: "The Research Begins",
    text: "Our 16-year-old son Micah fell down the beekeeping rabbit hole — books, videos, late-night notes on hive care, bee behavior, and everything in between.",
  },
  {
    icon: Home,
    title: "Two Hives Come Home",
    text: "Research turned into reality: two hives, right here at home. What started as his project didn't stay his for long.",
  },
  {
    icon: Users,
    title: "A Family Side Job",
    text: "One by one we all got pulled in — pouring candles, blending balms, wrapping soaps. His passion became something the whole family enjoys together.",
  },
  {
    icon: Sprout,
    title: "Growing in 2027",
    text: "More hives are coming in 2027, and with them our very first honey harvest in the spring. We can hardly wait to share it.",
  },
];

export function Story() {
  return (
    <section id="story" className="bg-[#F3EBE0] honey-grain" data-testid="story-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <Reveal className="max-w-2xl">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#D97706] font-semibold" data-testid="story-overline">Our Story</p>
          <h2 className="mt-4 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2C221E] tracking-tight" data-testid="story-heading">
            It started with one curious teenager and a stack of bee books.
          </h2>
          <p className="mt-5 text-base text-[#5A4D44] leading-relaxed" data-testid="story-text">
            McGee's Bees wasn't planned — it buzzed its way into our lives. Micah's fascination with bees
            became two backyard hives, and those hives became a family adventure. While the bees build up
            their honey for our first harvest in Spring 2027, we're keeping busy handcrafting goods with
            their beeswax: candles, lip balms, soaps, and lotions, all poured and blended in small batches at home.
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {MILESTONES.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.08}>
              <div
                className="h-full bg-white rounded-2xl p-6 border border-[#E6D7C3] hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`story-card-${i}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#F3EBE0] flex items-center justify-center">
                  <m.icon className="w-5 h-5 text-[#B45309]" />
                </div>
                <h3 className="mt-4 font-serif-display text-lg font-semibold text-[#2C221E]">{m.title}</h3>
                <p className="mt-2 text-sm text-[#5A4D44] leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 grid md:grid-cols-12 gap-6 items-stretch">
          <Reveal className="md:col-span-5" delay={0.05}>
            <div className="relative h-full rounded-2xl overflow-hidden border border-[#E6D7C3] shadow-lg rotate-[-1deg] hover:rotate-0 transition-transform duration-500">
              <img
                src="/images/micah.jpg"
                alt="Micah in his beekeeping suit, tending one of the family hives"
                className="w-full object-cover aspect-[4/5]"
                data-testid="story-hive-image"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-[#FAF5EE]/95 backdrop-blur-sm rounded-xl px-4 py-3 border border-[#E6D7C3]" data-testid="micah-caption">
                <p className="font-serif-display font-bold text-[#2C221E]">Micah</p>
                <p className="text-xs text-[#8C7A6B]">Founder & chief beekeeper — the one who started it all</p>
              </div>
            </div>
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.15}>
            <div className="h-full bg-[#2D4A3E] rounded-2xl p-8 flex flex-col justify-center">
              <p className="font-serif-display text-2xl font-semibold text-[#FAF5EE] leading-snug" data-testid="story-quote">
                "The bees taught us patience. The candles keep us busy until the honey's ready."
              </p>
              <p className="mt-4 text-sm text-[#E6D7C3]/80 font-mono-accent uppercase tracking-[0.15em]">— The McGee Family</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
